import { Injectable } from '@angular/core';
import { ElectronService } from '../electron/electron.service';

@Injectable({
  providedIn: 'root'
})
export class AppVisibilityService {

  constructor(private electronService: ElectronService) {}

  /**
   * Whether the app is currently visible/focused.
   * - Electron: uses document.hasFocus() — fires Electron notification window whenever
   *   the app is not the active window (including when behind other apps or on a second
   *   monitor). A duplicate in-app popup may appear on a second monitor, but missing a
   *   notification is a worse trade-off than seeing two.
   * - Web: uses document.visibilityState — correct for tab visibility.
   */
  public get isVisible(): boolean {
    if (this.electronService.isElectron) {
      return document.hasFocus();
    }
    return document.visibilityState === 'visible';
  }

  /**
   * Subscribes to the app becoming visible again after being hidden.
   * - Electron: listens to window 'focus' event (user actively brings the app forward)
   * - Web: listens to document 'visibilitychange' (tab becomes the active tab)
   *
   * @param callback Called once when the app becomes visible/focused
   * @returns Cleanup function to remove the listener
   */
  public onBecomeVisible(callback: () => void): () => void {
    if (this.electronService.isElectron) {
      const handler = () => {
        callback();
        window.removeEventListener('focus', handler);
      };
      window.addEventListener('focus', handler);
      return () => window.removeEventListener('focus', handler);
    }

    const handler = () => {
      if (document.visibilityState === 'visible') {
        callback();
        document.removeEventListener('visibilitychange', handler);
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }
}